import { useQuery } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { orpc } from '../../apis/orpc-query';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { serverMessage } from '../../lib/form-helpers';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { scenarioSummary } from '../compare/compare-display';
import { RunPicker } from '../compare/RunPicker';
import type { BuilderTest } from './report-display';

export interface ReportTestCardProps {
  index: number;
  vehicleIds: readonly string[];
  test: BuilderTest;
  canRemove: boolean;
  onChange: (runIds: Record<string, string[]>) => void;
  onRemove: () => void;
}

/**
 * One test in the builder: the scenario it is, and a run-picker per vehicle beneath it — the same picker step C
 * of the compare screen uses, so an engineer narrows a report's aggregation exactly as they narrow a
 * comparison's (plan §7).
 *
 * The universe of runs comes from `compare.compare` under `ALL_VALID`, which is by definition every comparable
 * run each vehicle has under this scenario. Everything is checked until the engineer unchecks something, at
 * which point the card starts posting an explicit selection — and a vehicle can never be emptied, because a
 * vehicle with no runs has nothing to report.
 */
export const ReportTestCard = ({ index, vehicleIds, test, canRemove, onChange, onRemove }: ReportTestCardProps) => {
  const { t } = useI18nTranslation();

  const universe = useQuery(
    orpc.compare.compare.queryOptions({
      input: { vehicleIds: [...vehicleIds], scenarioId: test.scenarioId, runSelection: 'ALL_VALID' },
      // A refused scenario is an answer, not a glitch worth retrying.
      retry: false,
    }),
  );

  const vehicles = universe.data?.vehicles ?? [];
  const label = t('reports.builder.testLabel', { number: index + 1 });

  const allRunIds = (): Record<string, string[]> => Object.fromEntries(vehicles.map((entry) => [entry.vehicle.id, entry.runs.map((run) => run.id)]));

  const onToggle = (vehicleId: string, runId: string): void => {
    const current = test.runIds ?? allRunIds();
    const kept = current[vehicleId] ?? [];
    const next = kept.includes(runId) ? kept.filter((id) => id !== runId) : [...kept, runId];

    // The last run of a vehicle stays checked: a column with no runs has nothing to aggregate.
    if (next.length === 0) {
      return;
    }

    onChange({ ...current, [vehicleId]: next });
  };

  return (
    <section
      className="rounded-lg border border-border bg-card p-4"
      data-test-index={index}
      data-scenario-id={test.scenarioId}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
          <p className="mt-0.5 text-sm font-medium text-card-foreground">
            {universe.data ? scenarioSummary(universe.data.scenario, t) : t('app.loading')}
          </p>
          {universe.data ? (
            <p className="mt-0.5 font-mono text-xs text-muted-foreground">
              {universe.data.scenario.code} · {universe.data.unit}
            </p>
          ) : null}
        </div>
        {canRemove ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label={t('reports.builder.removeTest', { label })}
            onClick={onRemove}
          >
            <Trash2 className="size-3.5" />
            {t('actions.remove')}
          </Button>
        ) : null}
      </div>

      {universe.isError ? (
        <p
          role="alert"
          className="mt-3 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive"
        >
          {serverMessage(universe.error) ?? t('errors.loadFailed')}
        </p>
      ) : null}

      {universe.isPending ? (
        <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
          <Spinner label={t('app.loading')} />
          {t('reports.builder.loadingRuns')}
        </div>
      ) : null}

      {universe.data ? (
        <div className="mt-3 grid gap-3 lg:grid-cols-2">
          {vehicles.map((entry) => (
            <RunPicker
              key={entry.vehicle.id}
              vehicleId={entry.vehicle.id}
              vehicleLabel={entry.vehicle.label}
              vehicleCode={entry.vehicle.code}
              runs={entry.runs}
              selectedRunIds={test.runIds?.[entry.vehicle.id] ?? entry.runs.map((run) => run.id)}
              unit={universe.data.unit}
              onToggle={onToggle}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
};
